import React from 'react'
import { Tooltip as ChakraTooltip, Box, Text, Portal } from '@chakra-ui/react'
import { useTheme } from '../../contexts/ThemeContext'

type NavTooltipProps = {
  label: React.ReactNode
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end'
  openDelay?: number
  closeDelay?: number
  gutter?: number
  hasArrow?: boolean
  children: React.ReactNode
  triggerProps?: React.ComponentProps<typeof Box>
}

export function Tooltip({
  label,
  placement = 'bottom',
  openDelay = 150,
  closeDelay = 50,
  gutter = 8,
  hasArrow = true,
  children,
  triggerProps,
}: NavTooltipProps) {
  const { colorMode } = useTheme()
  const isDark = colorMode === 'dark'

  // If no label, just return children without tooltip
  if (!label) {
    return <>{children}</>
  }

  // Simple fallback if ChakraTooltip structure is causing issues
  if (!ChakraTooltip?.Root) {
    return <>{children}</>
  }

  try {
    // Always wrap children in a span for consistent tooltip behavior
    // This ensures tooltips work with all component types (Button, IconButton, etc.)
    return (
      <ChakraTooltip.Root 
        openDelay={openDelay} 
        closeDelay={closeDelay}
        positioning={{ placement, gutter }}
      >
        <ChakraTooltip.Trigger asChild>
          <Box as="span" display="inline-block" {...triggerProps}>
            {children}
          </Box>
        </ChakraTooltip.Trigger>
        <Portal>
          <ChakraTooltip.Positioner zIndex={1500}>
            <ChakraTooltip.Content
              bg={isDark ? 'white' : 'gray.900'}
              border="1px solid"
              borderColor={isDark ? 'gray.200' : 'gray.700'}
              borderRadius="md"
              px={2.5}
              py={1.5}
              fontSize="sm"
              style={{
                backgroundColor: isDark ? '#ffffff' : '#1a202c',
                color: isDark ? '#1a202c' : '#ffffff',
                borderColor: isDark ? '#e2e8f0' : '#4a5568'
              }}
              _open={{
                animation: "fadeIn 0.2s ease-out"
              }}
            >
              <Box
                style={{
                  color: isDark ? '#1a202c' : '#ffffff'
                }}
                whiteSpace="pre-line"
              >
                {typeof label === 'string' ? (
                  <Text 
                    as="span" 
                    style={{
                      color: isDark ? '#1a202c' : '#ffffff'
                    }}
                  >
                    {label}
                  </Text>
                ) : (
                  <Box style={{ color: isDark ? '#1a202c' : '#ffffff' }}>
                    {label}
                  </Box>
                )}
              </Box>
              {hasArrow && <ChakraTooltip.Arrow />}
            </ChakraTooltip.Content>
          </ChakraTooltip.Positioner>
        </Portal>
      </ChakraTooltip.Root>
    )
  } catch (error) {
    // Fallback if tooltip fails to render
    console.warn('Tooltip render failed:', error)
    return <>{children}</>
  }
}

